/**
 * Page publique Plan de Table — /plan-de-table/:token
 * Lecture seule. Aucune auth. Mobile-first.
 * RGPD : emails/tél/tokens filtrés côté serveur.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ZoomIn, ZoomOut, Maximize2, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { computeSeatPositions, SITABLE_TYPES, SEAT_SIZE } from '../components/plan-table/seatUtils';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fonts
if (!document.getElementById('pdt-pub-fonts')) {
  const l = document.createElement('link');
  l.id = 'pdt-pub-fonts'; l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Montserrat:wght@400;600;700&display=swap';
  document.head.appendChild(l);
}

function anonymize(nom = '') {
  const parts = nom.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}

const DECOR_SET = new Set(['dj','dance','stage','buffet','bar','cake','photo','welcome','kids','door']);

// ─── Canvas lecture seule ─────────────────────────────────────────────────────
function ReadOnlyCanvas({ tables, invites, groups, brandColor }) {
  const [zoom,    setZoom]    = useState(1);
  const [tooltip, setTooltip] = useState(null);
  const pinch = useRef({ dist: null });
  const gold = brandColor || '#D4AF37';

  const groupMap = useMemo(() => {
    const m = {}; groups.forEach(g => { m[g.id] = g; }); return m;
  }, [groups]);
  const invBySeat = useMemo(() => {
    const m = {};
    invites.forEach(i => { if (i.table_id != null) m[`${i.table_id}-${i.seat_idx}`] = i; });
    return m;
  }, [invites]);

  const changeZoom = useCallback(d => setZoom(z => Math.min(2, Math.max(0.3, Math.round((z+d)*10)/10))), []);
  const onTouchStart = e => { if (e.touches.length===2) pinch.current.dist=Math.hypot(e.touches[0].clientX-e.touches[1].clientX,e.touches[0].clientY-e.touches[1].clientY); };
  const onTouchMove  = e => {
    if (e.touches.length!==2||!pinch.current.dist) return;
    const d=Math.hypot(e.touches[0].clientX-e.touches[1].clientX,e.touches[0].clientY-e.touches[1].clientY);
    changeZoom((d/pinch.current.dist-1)*0.4); pinch.current.dist=d;
  };

  return (
    <div style={{ position:'relative' }}>
      <p style={{ fontSize:10, color:'#C4B5A0', textAlign:'center', marginBottom:6 }} className="mo-hint">
        ← Glisse pour naviguer · Pince pour zoomer →
      </p>
      <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={()=>{pinch.current.dist=null;}}
        onClick={()=>setTooltip(null)}
        style={{ overflow:'auto', borderRadius:12, maxHeight:520, cursor:'default',
          background:'#FAF6EE', backgroundImage:'radial-gradient(circle,rgba(0,0,0,.03) 1px,transparent 1px)', backgroundSize:'24px 24px' }}>
        <div style={{ width:2800*zoom, height:1800*zoom, position:'relative', flexShrink:0 }}>
          <div style={{ position:'absolute', top:0, left:0, width:2800, height:1800, transform:`scale(${zoom})`, transformOrigin:'0 0' }}>
            {tables.map(t => {
              const w=t.w||120, h=t.h||120;
              const seats=SITABLE_TYPES.has(t.type)?computeSeatPositions(t.type,w,h,t.capacity||0):[];
              return (
                <div key={t.id} style={{ position:'absolute', left:t.x, top:t.y, transform:`rotate(${t.rotation||0}deg)`, transformOrigin:`${w/2}px ${h/2}px`, zIndex:2, userSelect:'none' }}>
                  <div style={{ width:w, height:h, borderRadius:t.type==='round'||t.type==='oval'?'50%':6, border:`2px solid ${gold}`, background:`${gold}12`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:10, color:'#0B1E20', textAlign:'center', padding:'0 4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'90%', pointerEvents:'none' }}>
                      {t.type==='honor'&&'👑 '}{t.label||t.type}
                    </span>
                    {!DECOR_SET.has(t.type)&&<span style={{ fontSize:8, color:'#9CA3AF', pointerEvents:'none' }}>{seats.filter(s=>invBySeat[`${t.id}-${s.idx}`]).length}/{t.capacity||0}</span>}
                  </div>
                  {seats.map(pos => {
                    const key=`${t.id}-${pos.idx}`;
                    const occ=invBySeat[key];
                    const grp=occ?groupMap[occ.group_id]:null;
                    return (
                      <div key={pos.idx}
                        onClick={e=>{e.stopPropagation();setTooltip({x:t.x+pos.x+SEAT_SIZE/2,y:t.y+pos.y-28,text:occ?anonymize(occ.nom):`Siège ${pos.idx+1} — Libre`});}}
                        style={{ position:'absolute', left:pos.x, top:pos.y, width:SEAT_SIZE, height:SEAT_SIZE, borderRadius:'50%', background:occ?(grp?.color??gold):'#FFFFFF', border:`2px solid ${grp?.color??gold}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:6, fontWeight:700, color:occ?'#FFFFFF':'#C58B19', cursor:'pointer', zIndex:5 }}>
                        {occ?occ.nom.charAt(0).toUpperCase():pos.idx+1}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {tooltip&&(
              <div style={{ position:'absolute', left:tooltip.x, top:tooltip.y, background:'#0B1E20', color:'#FFFFFF', fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:6, pointerEvents:'none', whiteSpace:'nowrap', zIndex:100, transform:'translateX(-50%)', fontFamily:"'Montserrat',sans-serif", boxShadow:'0 2px 8px rgba(0,0,0,.2)' }}>
                {tooltip.text}
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ position:'absolute', bottom:12, right:12, zIndex:50, display:'flex', flexDirection:'column', gap:4 }}>
        {[[ZoomIn,()=>changeZoom(0.1),'+'],[ZoomOut,()=>changeZoom(-0.1),'-'],[Maximize2,()=>setZoom(1),'↺']].map(([Icon,onClick,title])=>(
          <button key={title} onClick={onClick} style={{ width:30, height:30, borderRadius:7, background:'#FFFFFF', border:'1px solid #E5E7EB', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 1px 4px rgba(0,0,0,.08)' }}>
            <Icon size={13} style={{ color:'#374151' }} />
          </button>
        ))}
        <span style={{ fontSize:9, fontWeight:700, textAlign:'center', color:'#C58B19', background:'rgba(212,175,55,.1)', borderRadius:5, padding:'1px 4px' }}>{Math.round(zoom*100)}%</span>
      </div>
    </div>
  );
}

// ─── Ligne invité anonymisée ──────────────────────────────────────────────────
function GuestRow({ inv, group, tableName }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:'1px solid #F9F5EF' }}>
      <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background:group?.color??'#E5E7EB' }} />
      <span style={{ flex:1, fontSize:13, color:'#374151', fontWeight:500 }}>
        {anonymize(inv.nom)}
        {inv.age==='kid'&&<span style={{ fontSize:10, marginLeft:4 }}>👶</span>}
      </span>
      {tableName&&<span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:10, background:'rgba(93,188,191,.1)', color:'#0B4B4E', flexShrink:0 }}>{tableName}</span>}
      {inv.has_plus_one&&<span style={{ fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:10, background:'#F3F4F6', color:'#9CA3AF', flexShrink:0 }}>+1</span>}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PlanDeTablePage() {
  const { token } = useParams();
  const [state, setState] = useState('loading');
  const [plan,  setPlan]  = useState(null);

  useEffect(() => {
    // Sans token → hub plan de table (accès depuis Mon Organisation)
    if (!token) { setState('hub'); return; }
    fetch(`${BACKEND}/api/plan-de-table/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setPlan(d.plan); setState('ok'); })
      .catch(() => setState('404'));
  }, [token]);

  if (state==='loading') return (
    <div style={ctr}>
      <div style={{ width:32,height:32,border:'3px solid #D4AF37',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─── Hub sans token ───────────────────────────────────────────────────────
  if (state==='hub') return (
    <div style={{ minHeight:'100vh', background:'#FEFAF5', fontFamily:"'Montserrat',sans-serif", display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Montserrat:wght@400;600;700&display=swap');`}</style>
      <div style={{ maxWidth:480, width:'100%', textAlign:'center' }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🪑</div>
        <h1 style={{ ...pf28, marginBottom:8 }}>Plan de table</h1>
        <p style={{ color:'#9CA3AF', fontSize:13, lineHeight:1.6, marginBottom:32 }}>
          Accédez à votre plan de table depuis votre espace organisation.<br/>
          Créez, gérez les invités et visualisez le placement de tous vos convives.
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <Link to="/mon-organisation"
            style={{ background:'#D4AF37', color:'#fff', borderRadius:12, padding:'14px 24px', fontSize:14, fontWeight:700, textDecoration:'none', display:'block' }}>
            🏠 Accéder à mon organisation
          </Link>
          <Link to="/mon-organisation/plan-de-table/editeur"
            style={{ background:'rgba(212,175,55,0.1)', color:'#D4AF37', border:'1px solid rgba(212,175,55,0.3)', borderRadius:12, padding:'12px 24px', fontSize:13, fontWeight:600, textDecoration:'none', display:'block' }}>
            ✏️ Ouvrir l'éditeur de plan
          </Link>
          <Link to="/mon-organisation/plan-de-table/invites"
            style={{ background:'rgba(93,188,191,0.1)', color:'#0B4B4E', border:'1px solid rgba(93,188,191,0.3)', borderRadius:12, padding:'12px 24px', fontSize:13, fontWeight:600, textDecoration:'none', display:'block' }}>
            👥 Gérer mes invités
          </Link>
          <Link to="/"
            style={{ color:'#9CA3AF', fontSize:12, marginTop:8, textDecoration:'none', display:'block' }}>
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );

  if (state==='404') return (
    <div style={ctr}>
      <h1 style={pf28}>Plan introuvable</h1>
      <p style={{ color:'#9CA3AF',fontSize:13,fontFamily:"'Montserrat',sans-serif" }}>Ce lien est invalide ou a expiré.</p>
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:16, alignItems:'center' }}>
        <Link to="/plan-de-table" style={{ color:'#D4AF37', fontSize:12, fontFamily:"'Montserrat',sans-serif" }}>Accéder au plan de table</Link>
        <Link to="/" style={{ color:'#9CA3AF', fontSize:12, fontFamily:"'Montserrat',sans-serif" }}>Retour à l'accueil</Link>
      </div>
    </div>
  );

  const tables  = plan?.tables  ?? [];
  const invites = plan?.invites ?? [];
  const groups  = plan?.groups  ?? [];
  const groupMap={}, tableMap={};
  groups.forEach(g=>{groupMap[g.id]=g;}); tables.forEach(t=>{tableMap[t.id]=t;});
  const totalCap = tables.reduce((s,t)=>s+(t.capacity||0),0);
  const eventDate = plan?.date_evenement ? formatDate(plan.date_evenement) : null;

  return (
    <div style={{ minHeight:'100vh', background:'#FEFAF5', fontFamily:"'Montserrat',sans-serif" }}>
      {/* Header */}
      <header style={{ position:'sticky',top:0,zIndex:50,background:'rgba(254,250,245,.95)',backdropFilter:'blur(8px)',borderBottom:'1px solid #F0EAE0',padding:'0 20px',height:48,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
        <Link to="/" style={{ display:'flex',alignItems:'center',gap:8,textDecoration:'none' }}>
          <div style={{ width:28,height:28,borderRadius:6,background:'#D4AF37',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <span style={{ color:'#0B1E20',fontSize:12,fontWeight:800 }}>SE</span>
          </div>
          <span style={{ fontWeight:700,fontSize:14,color:'#0B1E20' }}>Show Événement</span>
        </Link>
        <span style={{ fontSize:10,color:'#D4AF37',fontFamily:'monospace',fontWeight:700,letterSpacing:'0.1em' }}>
          #{plan?.event_code||'—'}
        </span>
      </header>

      {/* Bandeau */}
      <section style={{ textAlign:'center',padding:'40px 20px 28px',borderBottom:'1px solid #F0EAE0' }}>
        <p style={{ fontSize:10,fontFamily:'monospace',color:'#D4AF37',textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:10 }}>ÉVÉNEMENT #{plan?.event_code}</p>
        <h1 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:'clamp(1.5rem,5vw,2.2rem)',color:'#1F2937',margin:'0 0 10px',lineHeight:1.2 }}>{plan?.nom_evenement||'Événement'}</h1>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:12,color:'#9CA3AF',fontSize:14,flexWrap:'wrap' }}>
          {eventDate&&<span>📅 {eventDate}</span>}
          {plan?.lieu&&<span>📍 {plan.lieu}</span>}
        </div>
      </section>

      <div style={{ maxWidth:900,margin:'0 auto',padding:'24px 16px 60px' }}>
        {/* Card canvas */}
        <div style={{ background:'#FFFFFF',borderRadius:18,boxShadow:'0 4px 20px rgba(0,0,0,.07)',border:'1px solid #F3F4F6',marginBottom:24,overflow:'hidden' }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:'1px solid #F9F5EF' }}>
            <span style={{ fontWeight:700,fontSize:15,color:'#1F2937' }}>Plan de la salle</span>
            <span style={{ fontSize:11,color:'#9CA3AF' }}>{tables.filter(t=>(t.capacity||0)>0).length} table{tables.filter(t=>(t.capacity||0)>0).length>1?'s':''} · {totalCap} place{totalCap>1?'s':''}</span>
          </div>
          <div style={{ padding:12 }}>
            {tables.length===0
              ?<p style={{ textAlign:'center',padding:'40px 20px',color:'#D1D5DB',fontSize:13 }}>Plan non encore configuré</p>
              :<ReadOnlyCanvas tables={tables} invites={invites} groups={groups} brandColor={plan?.brand_color} />
            }
          </div>
        </div>

        {/* Card liste */}
        {invites.length>0&&(
          <div style={{ background:'#FFFFFF',borderRadius:18,boxShadow:'0 4px 20px rgba(0,0,0,.07)',border:'1px solid #F3F4F6',overflow:'hidden' }}>
            <div style={{ display:'flex',alignItems:'center',gap:8,padding:'14px 18px',borderBottom:'1px solid #F9F5EF' }}>
              <Users size={16} style={{ color:'#D4AF37' }} />
              <span style={{ fontWeight:700,fontSize:15,color:'#1F2937' }}>Liste des invités</span>
              <span style={{ fontSize:11,color:'#9CA3AF',marginLeft:4 }}>{invites.length} invité{invites.length>1?'s':''}</span>
            </div>
            <div style={{ padding:'0 4px 8px' }}>
              <Tabs defaultValue="all">
                <TabsList style={{ margin:'8px 12px',width:'calc(100% - 24px)' }}>
                  <TabsTrigger value="all"      style={{ flex:1,fontSize:12 }}>Tous</TabsTrigger>
                  <TabsTrigger value="by-group" style={{ flex:1,fontSize:12 }}>Par groupe</TabsTrigger>
                  <TabsTrigger value="by-table" style={{ flex:1,fontSize:12 }}>Par table</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <div style={{ padding:'4px 12px 8px' }}>
                    {invites.map(inv=><GuestRow key={inv.id} inv={inv} group={groupMap[inv.group_id]} tableName={inv.table_id?tableMap[inv.table_id]?.label:null}/>)}
                  </div>
                </TabsContent>

                <TabsContent value="by-group">
                  <div style={{ padding:'4px 12px 8px' }}>
                    {groups.map(g=>{
                      const ms=invites.filter(i=>i.group_id===g.id);
                      if(!ms.length) return null;
                      return(
                        <div key={g.id} style={{ marginBottom:16 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:6,paddingBottom:6,borderBottom:`2px solid ${g.color}40` }}>
                            <div style={{ width:10,height:10,borderRadius:'50%',background:g.color,flexShrink:0 }}/>
                            <span style={{ fontWeight:700,fontSize:12,color:'#374151' }}>{g.name}</span>
                            <span style={{ fontSize:10,color:'#9CA3AF',marginLeft:4 }}>{ms.length}</span>
                          </div>
                          {ms.map(inv=><GuestRow key={inv.id} inv={inv} group={g} tableName={inv.table_id?tableMap[inv.table_id]?.label:null}/>)}
                        </div>
                      );
                    })}
                    {invites.filter(i=>!i.group_id).length>0&&(
                      <div><p style={{ fontSize:11,color:'#9CA3AF',fontWeight:600,marginBottom:4 }}>Sans groupe</p>
                        {invites.filter(i=>!i.group_id).map(inv=><GuestRow key={inv.id} inv={inv} group={null} tableName={inv.table_id?tableMap[inv.table_id]?.label:null}/>)}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="by-table">
                  <div style={{ padding:'4px 12px 8px' }}>
                    {tables.filter(t=>(t.capacity||0)>0).map(t=>{
                      const seated=invites.filter(i=>i.table_id===t.id);
                      return(
                        <div key={t.id} style={{ marginBottom:16 }}>
                          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,paddingBottom:6,borderBottom:'1px solid #F3F4F6' }}>
                            <span style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:14,color:'#1F2937' }}>{t.label}</span>
                            <span style={{ fontSize:10,color:'#9CA3AF' }}>{seated.length}/{t.capacity}</span>
                          </div>
                          {seated.length===0
                            ?<p style={{ fontSize:11,color:'#D1D5DB',fontStyle:'italic' }}>Table vide</p>
                            :seated.map(inv=><GuestRow key={inv.id} inv={inv} group={groupMap[inv.group_id]} tableName={null}/>)
                          }
                        </div>
                      );
                    })}
                    {invites.filter(i=>!i.table_id).length>0&&(
                      <div>
                        <p style={{ fontSize:11,color:'#9CA3AF',fontWeight:600,marginBottom:4,marginTop:8 }}>Non placés</p>
                        {invites.filter(i=>!i.table_id).map(inv=><GuestRow key={inv.id} inv={inv} group={groupMap[inv.group_id]} tableName={null}/>)}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ textAlign:'center',padding:'20px 16px 32px',borderTop:'1px solid #F0EAE0' }}>
        <p style={{ fontSize:11,color:'#C4B5A0',lineHeight:1.8 }}>
          Plan de table créé avec{' '}
          <Link to="/" style={{ color:'#D4AF37',fontWeight:600,textDecoration:'none' }}>Show Événement</Link>
        </p>
        <p style={{ fontSize:10,color:'#D1D5DB' }}>Les données personnelles sont protégées conformément au RGPD.</p>
      </footer>

      <style>{`@media(min-width:640px){.mo-hint{display:none!important}}`}</style>
    </div>
  );
}

const ctr = { minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12,background:'#FEFAF5',padding:40,fontFamily:"'Montserrat',sans-serif" };
const pf28 = { fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:28,color:'#1F2937',margin:0 };
